import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContextNotification } from '@pandiafi/components'
import { DateTime } from 'luxon'
import {
  Button,
  Badge,
  BadgeColor,
  Tooltip,
  Heading,
} from '@pandiafi/design-system'

import type { SharedArea } from '../../api/apiHooks/useAPIGetSharedAreas'
import { GymReservationModal } from './GymReservationModal'
import { ReleaseAccessModal } from './ReleaseAccessModal'
import type { RightOfAccess } from '../../api/apiHooks/useAPIGetRightsOfAccess'
import { getActiveGymRights, getPriceForDate } from './utils'
import { pickResidentDetails } from '../../utils/residentDetails'

import { todayISO } from 'utils/dateUtils'
import { ResidentDetailsHtml } from 'components/ResidentDetailsHtml/ResidentDetailsHtml'

type TenantAccessInfo = {
  id: number
  isContractParty: boolean
  isPayer: boolean
}

interface GymReservationProps {
  gymArea: SharedArea
  tenantAccessInfo: TenantAccessInfo
  rightsOfAccess: RightOfAccess[]
  readonly: boolean
}

export const GymReservation: React.FC<GymReservationProps> = ({
  gymArea,
  tenantAccessInfo,
  rightsOfAccess,
  readonly,
}) => {
  const { t, i18n } = useTranslation('gymBooking')
  const lang = i18n.language

  const [reserveModal, setReserveModal] = useState(false)
  const [releaseAccess, setReleaseAccess] = useState<RightOfAccess | null>(null)

  const gymRights = getActiveGymRights(rightsOfAccess)

  const releasableGymRight = gymRights.find((ra) => !ra.isTerminated)
  const hasTerminatingGymRight = gymRights.some(
    (ra) => ra.isTerminated === true
  )
  const hasOnlyTerminatingRights = hasTerminatingGymRight && !releasableGymRight
  const sortedGymRights = [...gymRights].sort(
    (a, b) =>
      DateTime.fromISO(a.startDate).toMillis() -
      DateTime.fromISO(b.startDate).toMillis()
  )

  const hasReachedLimit = gymArea.ownSeats >= gymArea.maxOwnSeats
  const hasAvailableSeats = gymArea.availableSeats > 0
  const canReserve =
    gymArea.canReserve && hasAvailableSeats && !hasReachedLimit && !readonly
  const hasReserveAccess =
    (tenantAccessInfo.isContractParty || tenantAccessInfo.isPayer) && !readonly
  const canReleaseAccess =
    Boolean(releasableGymRight) && hasReserveAccess && !readonly

  const handleReserve = () => {
    if (!canReserve) return
    setReserveModal(true)
  }

  const handleReleaseAccess = (right: RightOfAccess) => {
    const startDate = DateTime.fromISO(right.startDate).startOf('day')
    const today = DateTime.now().startOf('day')
    if (!canReleaseAccess || right.isTerminated === true || startDate > today)
      return

    setReleaseAccess(right)
  }

  const handleCloseReserveModal = () => {
    setReserveModal(false)
  }

  const handleCloseReleaseAccess = () => {
    setReleaseAccess(null)
  }

  const today = todayISO()
  const currentPrice = gymArea.monthlyPrices.find(
    (price) => price.validFrom <= today && price.validTo >= today
  )

  const formatDate = (date: string) =>
    DateTime.fromISO(date).toFormat('dd.MM.yyyy')
  const todayStart = DateTime.now().startOf('day')

  const locationLabel = `${gymArea.buildingPartAddress.streetName} ${gymArea.buildingPartAddress.streetNumber}`

  const latestTerminatingEndDate = gymRights
    .filter((ra) => ra.endDate)
    .reduce<string | null>((latest, ra) => {
      if (!ra.endDate) return latest
      if (!latest) return ra.endDate
      return DateTime.fromISO(ra.endDate) > DateTime.fromISO(latest)
        ? ra.endDate
        : latest
    }, null)

  const endDateText = latestTerminatingEndDate
    ? formatDate(latestTerminatingEndDate)
    : null

  const accessSummaryText = hasOnlyTerminatingRights
    ? t('reservation.accessSummary.terminating', { endDate: endDateText })
    : canReleaseAccess
      ? t('reservation.accessSummary.active', { count: gymRights.length })
      : t('reservation.accessSummary.none')

  const residentDetails = pickResidentDetails(gymArea, lang)

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <Heading element="h2" size="m" semibold>
          {t('reservation.title')}, {locationLabel}
        </Heading>

        <p>
          {accessSummaryText} {t('reservation.availabilitySentence')}{' '}
          <strong>
            {t('reservation.availabilitySentenceBold', {
              count: gymArea.availableSeats,
            })}
          </strong>
        </p>
      </div>

      <div className="flex flex-col gap-8">
        {sortedGymRights.length > 0 && (
          <>
            {sortedGymRights.map((right) => {
              const startDateValue = DateTime.fromISO(right.startDate).startOf(
                'day'
              )
              const startDate = formatDate(right.startDate)
              const endDate = right.endDate ? formatDate(right.endDate) : null
              const { isTerminated } = right
              const isFutureAccess = startDateValue > todayStart
              const rightPrice = getPriceForDate(gymArea, right.startDate)

              return (
                <div key={right.id} className="flex justify-between items-end">
                  <div>
                    <p className="font-semibold">
                      {right.buildingPartAddress.streetName}{' '}
                      {right.buildingPartAddress.streetNumber}
                    </p>
                    <p className="text-sm">
                      {endDate
                        ? t('reservation.validUntil', {
                            startDate,
                            endDate,
                          })
                        : t('reservation.validFrom', { startDate })}{' '}
                      {!isFutureAccess && (
                        <Badge
                          color={
                            isTerminated ? BadgeColor.RED : BadgeColor.GREEN
                          }
                        >
                          {isTerminated
                            ? t('reservation.endsOnLabel', { endDate })
                            : t('reservation.activeLabel')}
                        </Badge>
                      )}
                    </p>
                    {rightPrice && rightPrice.priceIncludingVat && (
                      <p className="text-sm">
                        {isFutureAccess
                          ? t('reservation.priceAtStart', {
                              price: rightPrice.priceIncludingVat,
                            })
                          : `${t('modal.priceLabel')} ${rightPrice.priceIncludingVat} ${t('modal.perMonth')}`}
                      </p>
                    )}
                  </div>
                  <div>
                    {isTerminated ? (
                      <Tooltip
                        tooltipClassName="mx-0 w-64 text-left"
                        triggerArea={
                          <span className="inline-block">
                            <Button small secondary disabled className="mt-2">
                              {t('reservation.buttonRelease')}
                            </Button>
                          </span>
                        }
                      >
                        {t('reservation.releaseAccessEnding')}
                      </Tooltip>
                    ) : isFutureAccess ? (
                      <Tooltip
                        tooltipClassName="mx-0 w-64 text-left"
                        triggerArea={
                          <span className="inline-block">
                            <Button small secondary disabled className="mt-2">
                              {t('reservation.buttonRelease')}
                            </Button>
                          </span>
                        }
                      >
                        {t('reservation.releaseAccessFuture')}
                      </Tooltip>
                    ) : hasReserveAccess &&
                      (!right.endDate ||
                        DateTime.fromISO(right.endDate) >
                          DateTime.now().endOf('month')) ? (
                      <Button
                        small
                        variant="delete"
                        onClick={() => handleReleaseAccess(right)}
                      >
                        {t('reservation.buttonRelease')}
                      </Button>
                    ) : (
                      <Tooltip
                        tooltipClassName="mx-0 w-64 text-left"
                        triggerArea={
                          <div>
                            <Button variant="delete" disabled small>
                              {t('reservation.buttonRelease')}
                            </Button>
                          </div>
                        }
                      >
                        {t('reservation.releaseAccessUnavailable')}
                      </Tooltip>
                    )}
                  </div>
                </div>
              )
            })}
          </>
        )}

        {hasReachedLimit ? (
          <ContextNotification type="warning">
            {t('reservation.reachedLimitInfo', {
              max: gymArea.maxOwnSeats,
            })}
          </ContextNotification>
        ) : (
          <div className="flex justify-between items-end">
            <div>
              {currentPrice && (
                <div className="mt-5">
                  <p className="font-semibold">{t('reservation.priceLabel')}</p>
                  <p>
                    {currentPrice.priceIncludingVat} {t('modal.perMonth')}
                  </p>
                </div>
              )}
              <p>
                {gymArea.buildingPartAddress.streetName}{' '}
                {gymArea.buildingPartAddress.streetNumber}{' '}
                {gymArea.buildingPartLocationDetails}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleReserve}
                variant="primary"
                disabled={!canReserve || !hasReserveAccess}
              >
                {t('reservation.buttonReserve')}
              </Button>
            </div>
          </div>
        )}
        <ResidentDetailsHtml html={residentDetails} />
      </div>

      {reserveModal && (
        <GymReservationModal
          gymArea={gymArea}
          onClose={handleCloseReserveModal}
        />
      )}

      {releaseAccess && (
        <ReleaseAccessModal
          rightOfAccess={releaseAccess}
          onClose={handleCloseReleaseAccess}
        />
      )}
    </div>
  )
}
